import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { downloadAsFile } from 'src/services/downloadAsFile'

export const CONTAINER_COLUMN = '__container__'

const PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

const SCORE_PLACEHOLDER_KEY = 'scorePlaceHolder'

// A page's labels, which buckets and cups use to find the page.
const PAGE_LABELS_KEY = 'labels'

// A page's items; each item's label is its Unique Item ID.
const ITEMS_KEY = 'items'
const ITEM_LABEL_KEY = 'label'

export interface PlaceholdersByKind {
    content: string[]
    scoring: string[]
    pageLabels: string[]
    itemLabels: string[]
}

// The field a placeholder sits in decides its kind; any field not listed holds content.
const KIND_BY_FIELD = new Map<string, keyof PlaceholdersByKind>([
    [SCORE_PLACEHOLDER_KEY, 'scoring'],
    [PAGE_LABELS_KEY, 'pageLabels'],
])

// An item's own label is its Unique Item ID; a label anywhere else is content.
const kindOf = (field: string | undefined, onItem: boolean): keyof PlaceholdersByKind =>
    onItem && field === ITEM_LABEL_KEY
        ? 'itemLabels'
        : (field && KIND_BY_FIELD.get(field)) || 'content'

export function extractPlaceholders(module: ModuleDTO): PlaceholdersByKind {
    const found: Record<keyof PlaceholdersByKind, Set<string>> = {
        content: new Set(),
        scoring: new Set(),
        pageLabels: new Set(),
        itemLabels: new Set(),
    }
    const visit = (value: unknown, field?: string, onItem = false) => {
        if (typeof value === 'string') {
            const into = found[kindOf(field, onItem)]
            const regex = new RegExp(PLACEHOLDER_REGEX)
            let match: RegExpExecArray | null
            while ((match = regex.exec(value)) !== null) {
                into.add(match[1])
            }
        } else if (Array.isArray(value)) {
            // Strings in an array (e.g. a page's labels) belong to the array's field.
            value.forEach((element) => visit(element, field, onItem))
        } else if (value && typeof value === 'object') {
            const isItem = field === ITEMS_KEY
            Object.entries(value).forEach(([childKey, child]) => visit(child, childKey, isItem))
        }
    }
    visit(module)
    const names = (kind: keyof PlaceholdersByKind) =>
        Array.from(found[kind]).filter((name) => name !== CONTAINER_COLUMN)
    return {
        content: names('content'),
        scoring: names('scoring'),
        pageLabels: names('pageLabels'),
        itemLabels: names('itemLabels'),
    }
}

export function getSpreadsheetColumns(module: ModuleDTO): string[] {
    const { content, scoring, pageLabels, itemLabels } = extractPlaceholders(module)
    return [
        ...Array.from(new Set([...content, ...scoring, ...pageLabels, ...itemLabels])),
        CONTAINER_COLUMN,
    ]
}

export function downloadSampleSpreadsheet(columns: string[], moduleName?: string) {
    downloadAsFile(
        new Blob([`${columns.join(',')}\n`], { type: 'text/csv' }),
        `${moduleName ?? 'import'}_sample.csv`
    )
}
